/** Popover related utilities. */
import {SectionItem} from 'common/components/items-menu/types';
import {capitalize, commaizeNumber} from 'common/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {BarType, SelectedBar} from 'common/report/funnels/charts/funnels-step-chart/types';
import {CountType, FunnelSegment} from 'common/report/funnels/models/types';
import {formatPercent} from 'common/report/funnels/util';

import {getGroupLabel} from './index';
import {ButtonOption} from './cohorts';

export interface SlottedContent {
  text: string;
  subTextLines: string[];
}

interface StepConversionMetrics {
  count: string;
  stepConvPercent: string;
  overallConvPercent: string;
  dropOffCount: string;
  stepDropOffPercent: string;
  overallDropOffPercent: string;
}

enum ComparisonPeriod {
  None = `NONE`,
  Previous = `PREVIOUS`,
  Current = `CURRENT`,
}

interface SlottedContentGeneratorArgs {
  metrics: StepConversionMetrics;
  unit: string;
  stepIdx: number;
  comparisonPeriod?: ComparisonPeriod;
}

interface PopoverOptions {
  canViewInFlows: boolean;
  colors: string[];
  dataGroup?: DataGroup;
  dataGroupName: string;
  disabledCreateCohortButtonMessage: string;
  disabledViewInFlowsButtonMessage: string;
  disableViewUsersButtonMessage: string;
}

function getViewInFlowsButtonOptions(hasComparison = false, index: number): ButtonOption[] {
  const stepLabel = `(Step ${index} -> ${index + 1})`;
  const mainButtonLabel = hasComparison ? `View as Flow from Current Period ${stepLabel}` : `View as Flow ${stepLabel}`;

  return [
    {
      label: mainButtonLabel,
      value: `view-in-flows`,
    },
  ];
}

export class ChartPopover {
  public bar: SelectedBar;
  private segments: FunnelSegment[];
  private options: Partial<PopoverOptions>;
  private hasComparison: boolean;

  constructor(
    segments: FunnelSegment[],
    bar: SelectedBar,
    options: Partial<PopoverOptions> = {},
    hasComparison = false,
  ) {
    this.segments = segments;
    this.bar = bar;
    this.options = options;
    this.hasComparison = hasComparison;

    if (options && options.colors && options.colors.length < segments.length) {
      throw new Error(`not enough colors provided`);
    }
  }

  public getButtonOptions(): SectionItem[] {
    let buttonOptions = [];
    const {index, barIdx, type} = this.bar;
    const isFirstStep = index === 0;

    const {dataGroupName} = this.options;
    buttonOptions.push({
      label: `View ${capitalize(dataGroupName)}`,
      icon: `team`,
      value: `view-users`,
      tooltip:
        type === BarType.CONVERTED
          ? `View ${dataGroupName} who completed this step`
          : `View ${dataGroupName} who dropped off from step 1`,
      ...(this.options.disableViewUsersButtonMessage
        ? {
            isDisabled: true,
            tooltip: this.options.disableViewUsersButtonMessage,
          }
        : {}),
    });

    if (this.options.canViewInFlows && !isFirstStep) {
      const viewInFlowsButtonOptions = getViewInFlowsButtonOptions(this.hasComparison, index);
      let disabledViewInFlowsButtonMessage = this.options.disabledViewInFlowsButtonMessage;

      if (this.segments[barIdx].prop === `$other`) {
        disabledViewInFlowsButtonMessage = `View as Flow is disabled for $other segment`;
      }

      if (disabledViewInFlowsButtonMessage) {
        viewInFlowsButtonOptions.forEach((option) => {
          // @ts-ignore TODO: SectionItem and ButtonOption are in a weird state of overlap has to be untangled
          option.isDisabled = true;
          // @ts-ignore
          option.tooltip = disabledViewInFlowsButtonMessage;
        });
      }

      buttonOptions = [...buttonOptions, ...viewInFlowsButtonOptions];
    }

    return buttonOptions;
  }

  public getTitle(stepLabels) {
    if (this.isSegmented()) {
      return this.segments[this.bar.barIdx].propName;
    } else {
      return stepLabels[this.bar.index];
    }
  }

  public isSegmented() {
    return this.hasComparison ? this.segments.length > 2 : this.segments.length > 1;
  }

  public isDropOff() {
    return this.bar.type === BarType.DROP_OFF;
  }

  public getSlottedContent(countType: CountType): SlottedContent[] {
    const {type: barType, index: stepIdx, barIdx: segmentIdx} = this.bar;
    const groupLabel = getGroupLabel(this.options.dataGroup, {lowerCase: true});
    const unit = countType === `unique` ? `unique ${groupLabel}` : `total times`;
    let slottedContent = [];

    if (this.hasComparison) {
      const currentSegmentIdx = segmentIdx % 2 === 1 ? segmentIdx : segmentIdx + 1;
      const pastSegmentIdx = currentSegmentIdx - 1;
      const currentMetrics = this.getConversionMetrics(currentSegmentIdx, stepIdx);
      const pastMetrics = this.getConversionMetrics(pastSegmentIdx, stepIdx);
      switch (barType) {
        case BarType.CONVERTED:
          slottedContent = [
            this.getConvertedSlotContent({
              metrics: currentMetrics,
              comparisonPeriod: ComparisonPeriod.Current,
              unit,
              stepIdx,
            }),
            this.getConvertedSlotContent({
              metrics: pastMetrics,
              comparisonPeriod: ComparisonPeriod.Previous,
              unit,
              stepIdx,
            }),
          ];
          break;
        case BarType.DROP_OFF: {
          if (stepIdx === 0) {
            break;
          }
          slottedContent = [
            ...this.getDropOffSlottedContent({
              metrics: currentMetrics,
              comparisonPeriod: ComparisonPeriod.Current,
              unit,
              stepIdx,
            }),
            ...this.getDropOffSlottedContent({
              metrics: pastMetrics,
              comparisonPeriod: ComparisonPeriod.Previous,
              unit,
              stepIdx,
            }),
          ];
          break;
        }
      }
    } else {
      const metrics = this.getConversionMetrics(segmentIdx, stepIdx);
      switch (barType) {
        case BarType.CONVERTED:
          slottedContent = [this.getConvertedSlotContent({metrics, unit, stepIdx})];
          break;
        case BarType.DROP_OFF: {
          if (stepIdx === 0) {
            break;
          }
          slottedContent = this.getDropOffSlottedContent({metrics, unit, stepIdx});
          break;
        }
      }
    }
    return slottedContent;
  }

  private getConvertedSlotContent({
    metrics,
    unit,
    stepIdx,
    comparisonPeriod = ComparisonPeriod.None,
  }: SlottedContentGeneratorArgs): SlottedContent {
    let content;
    if (comparisonPeriod === ComparisonPeriod.None) {
      content = {
        text: `${metrics.overallConvPercent} completed this step`,
        subTextLines: [
          ...(stepIdx > 1 ? [`${metrics.stepConvPercent} completed from previous step`] : []),
          `${metrics.count} ${unit}`,
        ],
      };
    } else {
      const periodLabel = comparisonPeriod === ComparisonPeriod.Current ? `current` : `past`;
      content = {
        text: `${metrics.overallConvPercent} completed in ${periodLabel} period`,
        subTextLines: [
          ...(stepIdx > 1 ? [`${metrics.stepConvPercent} completed from previous step`] : []),
          `${metrics.count} ${unit}`,
        ],
      };
    }
    return content;
  }

  private getDropOffSlottedContent({
    metrics,
    unit,
    stepIdx,
    comparisonPeriod = ComparisonPeriod.None,
  }: SlottedContentGeneratorArgs): SlottedContent[] {
    let slottedContent;
    if (comparisonPeriod === ComparisonPeriod.None) {
      slottedContent = [
        {
          text: `${metrics.overallDropOffPercent} dropped off from step 1`,
          subTextLines: [...(stepIdx > 1 ? [`${metrics.stepDropOffPercent} dropped from previous step`] : [])],
        },
        {
          text: `${metrics.dropOffCount} ${unit} dropped off`,
          subTextLines: [`${metrics.count} completed this step`],
        },
      ];
    } else {
      const periodLabel = comparisonPeriod === ComparisonPeriod.Current ? `current` : `past`;
      slottedContent = [
        {
          text: `${metrics.overallDropOffPercent} dropoff in ${periodLabel} period`,
          subTextLines: [
            ...(stepIdx > 1 ? [`${metrics.stepDropOffPercent} dropoff from previous step`] : []),
            `${metrics.dropOffCount} ${unit} dropped off`,
          ],
        },
      ];
    }
    return slottedContent;
  }

  private getConversionMetrics(segmentIdx: number, stepIdx: number): StepConversionMetrics {
    const step = this.segments[segmentIdx].steps[stepIdx];
    const {count, overall_conv_ratio: overallConvRatio, step_conv_ratio: stepConvRatio} = step;
    const previousStep = this.segments[segmentIdx].steps[stepIdx - 1];
    return {
      count: commaizeNumber(count),
      stepConvPercent: formatPercent(stepConvRatio) as string,
      overallConvPercent: formatPercent(overallConvRatio) as string,
      dropOffCount: commaizeNumber(previousStep ? previousStep.count - count : 0),
      stepDropOffPercent: formatPercent(1 - stepConvRatio) as string,
      overallDropOffPercent: formatPercent(1 - overallConvRatio) as string,
    };
  }
}

export enum PopoverTracking {
  CLICK_COHORT = `[Viewed popover] Create cohort button`,
  CANCEL_COHORT = `[Viewed popover] Cancel cohort`,
  CLICK_OPEN = `[Viewed popover] Click on a chart/cell`,
  SAVE_COHORT = `[Viewed popover] Save cohort`,
  SAVE_FAILED = `[Viewed popover] Save cohort - failed`,
  VIEW_USERS = `[Viewed popover] View Users`,
}
